import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Plus, Edit, Trash2, ExternalLink } from "lucide-react";
import CrudDialog from "../../components/CrudDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import TablePagination, { type PaginationMeta } from "../../components/TablePagination";
import { OFFER_TAB_PROPS } from "../../lib/offerTabs";

interface UnsubscribeLink {
  id: number;
  name?: string;
  link: string;
  created_at: string;
}

interface UnsubscribeLinksProps {
  offer: {
    id: number;
    name: string;
  };
  unsubscribe_links: UnsubscribeLink[];
  pagination: PaginationMeta;
  is_admin: boolean;
}

export default function UnsubscribeLinks({ offer, unsubscribe_links, pagination, is_admin }: UnsubscribeLinksProps) {
  const [crudDialogOpen, setCrudDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UnsubscribeLink | null>(null);
  const [itemToDelete, setItemToDelete] = useState<UnsubscribeLink | null>(null);

  const handleAdd = () => {
    setSelectedItem(null);
    setCrudDialogOpen(true);
  };

  const handleEdit = (item: UnsubscribeLink) => {
    setSelectedItem(item);
    setCrudDialogOpen(true);
  };

  const handleDelete = (item: UnsubscribeLink) => {
    setItemToDelete(item);
    setConfirmDialogOpen(true);
  };

  const handleCrudDialogChange = (open: boolean) => {
    setCrudDialogOpen(open);
    if (!open) {
      setSelectedItem(null);
    }
  };

  const handleConfirmDialogChange = (open: boolean) => {
    setConfirmDialogOpen(open);
    if (!open) {
      setItemToDelete(null);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Unsubscribe Links</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage unsubscribe links for this offer
          </p>
        </div>
        {is_admin && (
          <Button
            onClick={handleAdd}
            className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Unsubscribe Link
          </Button>
        )}
      </div>

      <Card className="py-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  {is_admin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {unsubscribe_links.length === 0 ? (
                  <tr>
                    <td colSpan={is_admin ? 4 : 3} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="rounded-full bg-gray-100 p-3 mb-4">
                          <Plus className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No unsubscribe links added yet</p>
                        {is_admin && (
                          <p className="text-gray-400 text-xs mt-1">Click "Add Unsubscribe Link" to get started</p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  unsubscribe_links.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.name || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                          >
                            {item.link}
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </td>
                      {is_admin && (
                        <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(item)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <TablePagination
        pagination={pagination}
        reloadOnly={OFFER_TAB_PROPS.unsubscribe_links}
        pageParam="unsubscribe_links_page"
        label="unsubscribe links"
      />

      <CrudDialog
        open={crudDialogOpen}
        onOpenChange={handleCrudDialogChange}
        title={selectedItem ? "Edit Unsubscribe Link" : "Add Unsubscribe Link"}
        description={
          selectedItem
            ? "Update the unsubscribe link URL."
            : "Add a new unsubscribe link for this offer."
        }
        offerId={offer.id}
        resourceType="unsubscribe_links"
        item={selectedItem}
      />

      {itemToDelete && (
        <ConfirmationDialog
          open={confirmDialogOpen}
          onOpenChange={handleConfirmDialogChange}
          title="Delete Unsubscribe Link"
          description={`Are you sure you want to delete "${itemToDelete.link}"? This action cannot be undone.`}
          offerId={offer.id}
          resourceType="unsubscribe_links"
          itemId={itemToDelete.id}
        />
      )}
    </>
  );
}
